import { useRef, useState } from 'react'
import {
  Button,
  CalendarCell,
  CalendarGrid,
  Dialog,
  DialogTrigger,
  Heading,
  Popover,
  RangeCalendar,
} from 'react-aria-components'
import type { RangeValue } from 'react-aria-components'
import { CalendarDate, getLocalTimeZone, parseDate, today } from '@internationalized/date'
import { Check, ChevronLeft, ChevronRight, X } from '@bloo-team/ui/icons'
import { Icon } from '@bloo-team/ui'
import { DURATION_PRESETS, DURATION_UNITS, timeLabel, toApiTime } from '@bloo-team/api-client'
import type { DqlTime } from '@bloo-team/api-client'
import { lastInputWasKeyboard } from '@bloo-team/ui'

/**
 * The builder's time control: a chip-height trigger opening one two-row
 * popover holding all three ways of saying "when":
 *
 *   row 1 — six preset chips (15m..30d, each ~5x the last, so anything wanted
 *           is near one) and, INLINE with them, the custom "last N unit" form
 *           for the in-between cases the presets deliberately dropped;
 *   row 2 — absolute from→to, for investigations anchored to an incident
 *           window rather than to now: a two-month range calendar (pick a
 *           start day, pick an end day, the days between paint — the flight-
 *           booking idiom) over one row per boundary holding its clock time
 *           and a 24-hour strip that paints the same way for hours. Start and
 *           end are one connected selection, not two independent inputs.
 *
 * Everything is visible at once — no tabs, no modes — and committing any form
 * closes the popover: the question "when?" has one answer. The custom and
 * absolute forms submit on Enter; their buttons are ✓ glyphs, not labelled
 * buttons, to keep each row one control tall and the popover small.
 */
export function TimeRangePicker({
  value,
  onChange,
  disabled = false,
  onCommitted,
  presets = DURATION_PRESETS,
  relativeForm = true,
}: {
  value: DqlTime
  onChange: (next: DqlTime) => void
  disabled?: boolean
  /** The quick-range chips; Search's six by default. */
  presets?: ReadonlyArray<{ value: string; label: string }>
  /** Show the "last N unit" form beside the chips (Search: yes). */
  relativeForm?: boolean
  /**
   * Fires after a choice is committed and the popover has closed — the
   * builder uses it to walk focus onward to the next control (`+ filter`),
   * so the keyboard journey never stalls on an already-answered question.
   */
  onCommitted?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [customN, setCustomN] = useState('45')
  const [customUnit, setCustomUnit] = useState('m')
  // Absolute range: the calendar owns the days, two clock inputs own the
  // times. Seeded from the current value when it is already absolute, so
  // reopening shows the window being edited rather than a blank form.
  const seeded = value.kind === 'absolute' ? parseAbsolute(value) : null
  const [range, setRange] = useState<RangeValue<CalendarDate> | null>(seeded?.range ?? null)
  const [fromTime, setFromTime] = useState(seeded?.fromTime ?? '00:00')
  const [toTime, setToTime] = useState(seeded?.toTime ?? '23:59')
  const dialogRef = useRef<HTMLDivElement>(null)
  /** Same close-then-refocus reopen guard as PickList. */
  const suppressAutoOpen = useRef(false)

  /** Escape / ✕ sets this so the close that follows reverts, not commits. */
  const cancelling = useRef(false)

  const commit = (next: DqlTime) => {
    onChange(next)
    suppressAutoOpen.current = true
    setOpen(false)
    // After close, focus restore runs first; then hand focus onward.
    if (onCommitted) setTimeout(onCommitted, 0)
  }

  const absoluteNext = (): DqlTime | null =>
    absoluteValid ? { kind: 'absolute', start: toApiTime(from), end: toApiTime(to) } : null

  /** Put the absolute form back to what the current value says. */
  const revertAbsolute = () => {
    const s = value.kind === 'absolute' ? parseAbsolute(value) : null
    setRange(s?.range ?? null)
    setFromTime(s?.fromTime ?? '00:00')
    setToTime(s?.toTime ?? '23:59')
  }

  /**
   * Closing by clicking outside COMMITS a valid absolute range — people
   * configure the window and click away, treating that as "done". Only the
   * explicit ✕ or Escape throws the edit away. Nothing happens when the
   * form is untouched or invalid.
   */
  const onClose = () => {
    suppressAutoOpen.current = true
    if (cancelling.current) {
      cancelling.current = false
      revertAbsolute()
      return
    }
    const next = absoluteNext()
    if (next && !sameTime(next, value)) {
      onChange(next)
      if (onCommitted) setTimeout(onCommitted, 0)
    }
  }

  const cancel = () => {
    cancelling.current = true
    setOpen(false)
    onClose()
  }

  /**
   * Arrow keys walk the popover: ←/→ between preset chips, ↓ from the chips
   * into the custom row and again into the absolute row, ↑ back. Left/right
   * are only claimed while ON a chip — inside the text inputs they must keep
   * moving the caret.
   */
  const onDialogKeyDown = (e: React.KeyboardEvent) => {
    const t = e.target as HTMLElement
    const root = dialogRef.current
    if (!root) return
    // Escape is cancel: mark it so the dismiss that RAC fires next reverts.
    if (e.key === 'Escape') {
      cancelling.current = true
      return
    }
    const chips = [...root.querySelectorAll<HTMLElement>('.trp-preset')]
    const onPreset = chips.includes(t)
    // The calendar and the hour strips own their own arrow keys.
    if (t.closest('.trp-cal, .trp-hours')) return

    if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && onPreset) {
      e.preventDefault()
      const i = chips.indexOf(t)
      const next = chips[(i + (e.key === 'ArrowRight' ? 1 : chips.length - 1)) % chips.length]
      next?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      const day = root.querySelector<HTMLElement>('.trp-cal [role="gridcell"] [tabindex="0"]')
      if (onPreset) (root.querySelector<HTMLElement>('.trp-n') ?? day)?.focus()
      else day?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (t.closest('.trp-abs')) (root.querySelector<HTMLElement>('.trp-n') ?? chips[0])?.focus()
      else chips[0]?.focus()
    }
  }

  const customValid = /^\d+$/.test(customN.trim()) && Number(customN) > 0
  const from = range ? `${range.start.toString()}T${fromTime}` : ''
  const to = range ? `${range.end.toString()}T${toTime}` : ''
  const absoluteValid = range !== null && TIME_RE.test(fromTime) && TIME_RE.test(toTime) && from < to
  const sameDay = range !== null && range.start.compare(range.end) === 0
  const setHour = (which: 'from' | 'to', h: number) => {
    const cur = which === 'from' ? fromTime : toTime
    const mins = TIME_RE.test(cur) ? cur.slice(3) : '00'
    ;(which === 'from' ? setFromTime : setToTime)(`${String(h).padStart(2, '0')}:${mins}`)
  }

  return (
    <DialogTrigger
      isOpen={open}
      onOpenChange={(v) => {
        if (!v) onClose()
        setOpen(v)
      }}
    >
      <Button
        className="pick-btn"
        aria-label="Time range"
        isDisabled={disabled}
        onFocus={() => {
          if (suppressAutoOpen.current || !lastInputWasKeyboard()) return
          setOpen(true)
        }}
        onBlur={() => {
          suppressAutoOpen.current = false
        }}
        // Same as PickList: `/` opens a focused-but-closed trigger.
        onKeyDown={(e) => {
          if (e.key === '/') {
            e.preventDefault()
            suppressAutoOpen.current = false
            setOpen(true)
          }
        }}
      >
        {timeLabel(value)}
        <span className="pick-caret" aria-hidden="true">▾</span>
      </Button>
      <Popover className="pick-pop" placement="bottom start">
        <Dialog className="pick-dialog trp" aria-label="Time range">
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- key
              routing for focus management, not an interactive control */}
          <div ref={dialogRef} className="trp-keys" onKeyDown={onDialogKeyDown}>
          <div className="trp-row">
            <span role="group" aria-label="Quick ranges" className="trp-presets">
              {presets.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  className="trp-preset"
                  aria-pressed={value.kind === 'relative' && value.duration === d.value}
                  onClick={() => commit({ kind: 'relative', duration: d.value })}
                >
                  {d.label}
                </button>
              ))}
            </span>
            {relativeForm ? <span className="trp-sep" aria-hidden="true" /> : null}
            {relativeForm ? (
              <form
                className="trp-inline"
                aria-label="Custom duration"
                onSubmit={(e) => {
                  e.preventDefault()
                  // React portals bubble synthetic events through the COMPONENT
                  // tree, so without this the submit reaches the builder's own
                  // <form> and runs the query with the not-yet-committed time.
                  e.stopPropagation()
                  if (customValid) commit({ kind: 'relative', duration: `${customN.trim()}${customUnit}` })
                }}
              >
                <span className="trp-label">last</span>
                <input
                  className="trp-n"
                  aria-label="Amount"
                  inputMode="numeric"
                  value={customN}
                  onChange={(e) => setCustomN(e.target.value)}
                />
                <select aria-label="Unit" value={customUnit} onChange={(e) => setCustomUnit(e.target.value)}>
                  {DURATION_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>{u.value}</option>
                  ))}
                </select>
                <button type="submit" className="trp-go" aria-label="Apply custom duration" disabled={!customValid}>
                  <Icon icon={Check} size={12} />
                </button>
              </form>
            ) : null}
          </div>

          <form
            className="trp-abs"
            aria-label="Absolute range"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation() // same portal-bubbling hazard as above
              if (absoluteValid) commit({ kind: 'absolute', start: toApiTime(from), end: toApiTime(to) })
            }}
          >
            <RangeCalendar
              className="trp-cal"
              aria-label="Days"
              visibleDuration={{ months: 2 }}
              maxValue={today(getLocalTimeZone())}
              value={range}
              onChange={setRange}
            >
              <header className="trp-cal-head">
                <Button slot="previous" className="trp-cal-nav" aria-label="Previous month">
                  <Icon icon={ChevronLeft} size={12} />
                </Button>
                <Heading className="trp-cal-title" />
                <Button slot="next" className="trp-cal-nav" aria-label="Next month">
                  <Icon icon={ChevronRight} size={12} />
                </Button>
              </header>
              <div className="trp-cal-months">
                {[0, 1].map((m) => (
                  <CalendarGrid key={m} className="trp-cal-grid" offset={{ months: m }} weekdayStyle="short">
                    {(date) => <CalendarCell className="trp-cal-cell" date={date} />}
                  </CalendarGrid>
                ))}
              </div>
            </RangeCalendar>

            <div className="trp-bound">
              <span className="trp-label">from</span>
              <span className="trp-day mono">{range ? dayLabel(range.start) : '—'}</span>
              <input
                type="time"
                aria-label="Start time"
                value={fromTime}
                disabled={!range}
                onChange={(e) => setFromTime(e.target.value)}
              />
              <HourStrip
                label="Start hour"
                paintFrom={hourOf(fromTime)}
                paintTo={sameDay ? hourOf(toTime) : 23}
                disabled={!range}
                onPick={(h) => setHour('from', h)}
              />
            </div>
            <div className="trp-bound">
              <span className="trp-label">to</span>
              <span className="trp-day mono">{range ? dayLabel(range.end) : '—'}</span>
              <input
                type="time"
                aria-label="End time"
                value={toTime}
                disabled={!range}
                onChange={(e) => setToTime(e.target.value)}
              />
              <HourStrip
                label="End hour"
                paintFrom={sameDay ? hourOf(fromTime) : 0}
                paintTo={hourOf(toTime)}
                disabled={!range}
                onPick={(h) => setHour('to', h)}
              />
            </div>

            <div className="trp-row trp-abs-foot">
              <span className="trp-span" role="status">
                {absoluteValid ? spanLabel(from, to) : range ? 'end must be after start' : 'pick a start and end day'}
              </span>
              <span className="trp-inline">
                <button type="button" className="trp-go trp-cancel" aria-label="Cancel absolute range" onClick={cancel}>
                  <Icon icon={X} size={12} />
                </button>
                <button type="submit" className="trp-go" aria-label="Apply absolute range" disabled={!absoluteValid}>
                  <Icon icon={Check} size={12} />
                </button>
              </span>
            </div>
          </form>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
}

const TIME_RE = /^\d{2}:\d{2}$/

function sameTime(a: DqlTime, b: DqlTime): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'relative' && b.kind === 'relative') return a.duration === b.duration
  if (a.kind === 'absolute' && b.kind === 'absolute') return a.start === b.start && a.end === b.end
  return false
}

function hourOf(t: string): number {
  return TIME_RE.test(t) ? Number(t.slice(0, 2)) : 0
}

function parseAbsolute(v: { start: string; end: string }): {
  range: RangeValue<CalendarDate>
  fromTime: string
  toTime: string
} | null {
  try {
    return {
      range: { start: parseDate(v.start.slice(0, 10)), end: parseDate(v.end.slice(0, 10)) },
      fromTime: v.start.slice(11, 16),
      toTime: v.end.slice(11, 16),
    }
  } catch {
    return null
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function dayLabel(d: CalendarDate): string {
  return `${MONTHS[d.month - 1]} ${String(d.day).padStart(2, '0')}`
}

/** "2d 3h 30m" between two `YYYY-MM-DDTHH:mm` locals. */
function spanLabel(from: string, to: string): string {
  const ms = new Date(to).getTime() - new Date(from).getTime()
  const mins = Math.round(ms / 60_000)
  const d = Math.floor(mins / 1440)
  const h = Math.floor((mins % 1440) / 60)
  const m = mins % 60
  return [d && `${d}d`, h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ') || '0m'
}

/**
 * Twenty-four hour cells; those from `paintFrom` to `paintTo` inclusive are
 * painted, so the hours of the window read the same way the days do in the
 * calendar above. Clicking a cell sets that boundary's hour (minutes kept).
 * ←/→ move between cells, the strip is one tab stop.
 */
function HourStrip({
  label,
  paintFrom,
  paintTo,
  disabled,
  onPick,
}: {
  label: string
  paintFrom: number
  paintTo: number
  disabled: boolean
  onPick: (hour: number) => void
}) {
  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- roving-
       tabindex arrow routing for the buttons inside; the group itself is not a control */
    <span
      className="trp-hours"
      role="group"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
        e.preventDefault()
        const cells = [...e.currentTarget.querySelectorAll<HTMLElement>('button')]
        const i = cells.indexOf(e.target as HTMLElement)
        if (i < 0) return
        cells[(i + (e.key === 'ArrowRight' ? 1 : 23)) % 24]?.focus()
      }}
    >
      {Array.from({ length: 24 }, (_, h) => (
        <button
          key={h}
          type="button"
          className="trp-hour"
          data-painted={h >= paintFrom && h <= paintTo ? '' : undefined}
          data-edge={h === paintFrom || h === paintTo ? '' : undefined}
          aria-label={`${String(h).padStart(2, '0')}:00`}
          tabIndex={h === (label === 'Start hour' ? paintFrom : paintTo) ? 0 : -1}
          disabled={disabled}
          onClick={() => onPick(h)}
        />
      ))}
    </span>
  )
}
